import React, { useState, useMemo, useEffect } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Clock,
  User,
  Calendar as CalendarIcon,
  Search,
  Filter,
  MoreHorizontal,
  CheckCircle2,
  AlertCircle,
  Users,
  Download
} from 'lucide-react';
import { Issue, User as UserType, Project } from '../../types';
import { apiService } from '../../services/api';
import { toast } from '../UI/Toast';

interface ProjectCalendarProps {
  project: Project;
  issues: Issue[];
  users: UserType[];
  onCreateIssue: (projectId: string) => void;
  onIssueClick: (issue: Issue) => void;
}

interface CalendarEvent {
  id: string;
  title: string;
  description: string;
  startTime: string;
  endTime: string;
  duration: string;
  assignee: string;
  category: string;
  status: 'pending' | 'in-progress' | 'completed' | 'important';
  color: string;
  date: string;
}

export const ProjectCalendar: React.FC<ProjectCalendarProps> = ({
  project,
  issues,
  users,
  onCreateIssue,
  onIssueClick
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<'week' | 'month'>('week');
  const [showWaitingList, setShowWaitingList] = useState(true);
  const [assigneeFilter, setAssigneeFilter] = useState<string>('all');
  const [projectMembers, setProjectMembers] = useState<UserType[]>([]);
  const [projectMembersLoading, setProjectMembersLoading] = useState(false);

  // Load project members
  useEffect(() => {
    const loadProjectMembers = async () => {
      try {
        setProjectMembersLoading(true);
        const members = await apiService.getProjectMembers(project.id);
        setProjectMembers(members);
      } catch (error) {
        console.error('Failed to load project members', error);
        toast.error('Failed to load project members');
      } finally {
        setProjectMembersLoading(false);
      }
    };

    loadProjectMembers();
  }, [project.id]);

  // Generate calendar events from issues
  const calendarEvents = useMemo(() => {
    const events: CalendarEvent[] = [];
    
    // Convert issues to calendar events
    issues.forEach((issue) => {
      if (issue.dueDate) {
        // Filter by assignee if a specific assignee is selected
        if (assigneeFilter !== 'all' && assigneeFilter !== 'unassigned' && issue.assignee !== assigneeFilter) {
          return;
        }
        if (assigneeFilter === 'unassigned' && issue.assignee) {
          return;
        }

        const dueDate = new Date(issue.dueDate);
        const assignee = users.find(u => u.id === issue.assignee);

        // Generate realistic time slots based on issue properties
        const timeSlots = [
          { start: '09:00', end: '11:00' },
          { start: '11:00', end: '13:00' },
          { start: '14:00', end: '16:00' },
          { start: '16:00', end: '18:00' }
        ];
        const timeSlot = timeSlots[Math.floor(Math.random() * timeSlots.length)];
        
        events.push({
          id: issue.id,
          title: issue.summary,
          description: issue.description || '',
          startTime: timeSlot.start,
          endTime: timeSlot.end,
          duration: (issue.estimationTime && issue.estimationTime > 0) ? `${issue.estimationTime}h` : '2h',
          assignee: assignee?.displayName || 'Unassigned',
          category: issue.type,
          status: issue.status === 'done' ? 'completed' : 
                  issue.status === 'in-progress' ? 'in-progress' : 
                  issue.priority === 'highest' ? 'important' : 'pending',
          color: getEventColor(issue.type, issue.status),
          date: dueDate.toISOString().split('T')[0]
        });
      }
    });

    return events;
  }, [issues, users, assigneeFilter]);

  // Waiting list items - issues without due dates
  const waitingListItems = useMemo(() => {
    return issues
      .filter(issue => {
        // Filter by due date
        if (issue.dueDate) return false;

        // Filter by assignee if a specific assignee is selected
        if (assigneeFilter !== 'all' && assigneeFilter !== 'unassigned' && issue.assignee !== assigneeFilter) {
          return false;
        }
        if (assigneeFilter === 'unassigned' && issue.assignee) {
          return false;
        }

        return true;
      })
      .slice(0, 7) // Limit to 7 items for display
      .map(issue => {
        const assignee = users.find(u => u.id === issue.assignee);
        return {
          id: issue.id,
          title: issue.summary,
          category: issue.type,
          duration: (issue.estimationTime && issue.estimationTime > 0) ? `${issue.estimationTime}h` : '2h',
          assignee: assignee?.displayName || 'Unassigned',
          status: issue.status === 'done' ? 'completed' : 'pending'
        };
      });
  }, [issues, users, assigneeFilter]);

  function getDateString(daysFromToday: number): string {
    const date = new Date();
    date.setDate(date.getDate() + daysFromToday);
    return date.toISOString().split('T')[0];
  }

  function getEventColor(type: string, status: string): string {
    if (status === 'done') return '#10B981';
    if (status === 'in-progress') return '#3B82F6';
    
    switch (type) {
      case 'bug': return '#EF4444';
      case 'story': return '#10B981';
      case 'task': return '#3B82F6';
      case 'epic': return '#8B5CF6';
      default: return '#6B7280';
    }
  }

  const getWeekDays = () => {
    const start = new Date(currentDate);
    const day = start.getDay();
    const diff = start.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
    start.setDate(diff);

    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      days.push(date);
    }
    return days;
  };

  const weekDays = getWeekDays();
  const today = new Date().toDateString();

  const getEventsForDate = (date: Date) => {
    const dateString = date.toISOString().split('T')[0];
    return calendarEvents.filter(event => event.date === dateString);
  };

  const getUserById = (userId: string) => {
    return users.find(u => u.id === userId);
  };

  const getAssigneeAvatar = (assigneeName: string) => {
    const user = users.find(u => u.displayName === assigneeName);
    return user?.avatarUrl || 'https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg?auto=compress&cs=tinysrgb&w=50&h=50&dpr=2';
  };

  const navigateWeek = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + (direction === 'next' ? 7 : -7));
    setCurrentDate(newDate);
  };

  const formatMonth = (date: Date) => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="w-3 h-3 text-green-600" />;
      case 'important':
        return <AlertCircle className="w-3 h-3 text-red-600" />;
      case 'in-progress':
        return <Clock className="w-3 h-3 text-blue-600" />;
      default:
        return <Clock className="w-3 h-3 text-gray-400" />;
    }
  };

  const exportToCSV = () => {
    const weekDays = getWeekDays();
    const csvData = [];

    // Header row
    csvData.push(['Day', 'Date', 'Task', 'Assignee', 'Status', 'Priority', 'Type', 'Duration', 'Description']);

    // Add data for each day
    weekDays.forEach(day => {
      const dayEvents = calendarEvents.filter(event => event.date === day.toISOString().split('T')[0]);
      const dayName = day.toLocaleDateString('en-US', { weekday: 'long' });
      const dateString = day.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

      if (dayEvents.length === 0) {
        csvData.push([dayName, dateString, 'No tasks scheduled', '', '', '', '', '', '']);
      } else {
        dayEvents.forEach(event => {
          csvData.push([
            dayName,
            dateString,
            event.title,
            event.assignee,
            event.status,
            '', // Priority - we don't have this in the event object
            event.category,
            event.duration,
            event.description
          ]);
        });
      }
    });

    // Convert to CSV string
    const csvContent = csvData.map(row =>
      row.map(field => `"${field}"`).join(',')
    ).join('\n');

    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `calendar-${formatMonth(currentDate).replace(' ', '-')}-week.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="h-full flex bg-gray-50">
      {/* Main Calendar Area */}
      <div className="flex-1 flex flex-col">
        {/* Calendar Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4">
              <h2 className="text-2xl font-semibold text-gray-900">Calendar</h2>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => navigateWeek('prev')}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <ChevronLeft className="w-5 h-5 text-gray-600" />
                </button>
                <span className="text-lg font-medium text-gray-900 min-w-48 text-center">
                  {formatMonth(currentDate)}
                </span>
                <button
                  onClick={() => navigateWeek('next')}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <ChevronRight className="w-5 h-5 text-gray-600" />
                </button>
              </div>

              {/* Assignee Filter Dropdown */}
              <div className="flex items-center space-x-2">
                <User className="w-4 h-4 text-gray-500" />
                <select
                  value={assigneeFilter}
                  onChange={(e) => setAssigneeFilter(e.target.value)}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  disabled={projectMembersLoading}
                >
                  <option value="all">All Team Members</option>
                  <option value="unassigned">Unassigned</option>
                  {projectMembers.map(member => (
                    <option key={member.id} value={member.id}>
                      {member.displayName}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <button
                onClick={() => onCreateIssue(project.id)}
                className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span>Add Task</span>
              </button>
              <button
                onClick={exportToCSV}
                className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                <Download className="w-4 h-4" />
                <span>Export Week</span>
              </button>
              <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                <Search className="w-5 h-5" />
              </button>
              <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                <Filter className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Calendar Grid */}
        <div className="flex-1 overflow-auto">
          <div className="min-h-full">
            {/* Week Header */}
            <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
              <div className="grid grid-cols-8 gap-0">
                {/* Time column header */}
                <div className="p-4 border-r border-gray-200">
                  <div className="text-sm font-medium text-gray-500">Time</div>
                </div>
                
                {/* Day headers */}
                {weekDays.map((day, index) => (
                  <div key={index} className="p-4 border-r border-gray-200 last:border-r-0">
                    <div className="text-center">
                      <div className="text-sm font-medium text-gray-500 mb-1">
                        {day.toLocaleDateString('en-US', { weekday: 'short' })}
                      </div>
                      <div className={`text-lg font-semibold ${
                        day.toDateString() === today ? 'text-blue-600' : 'text-gray-900'
                      }`}>
                        {day.getDate()}
                      </div>
                      {day.toDateString() === today && (
                        <div className="w-2 h-2 bg-blue-600 rounded-full mx-auto mt-1"></div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Calendar Body */}
            <div className="bg-white">
              <div className="grid grid-cols-8 gap-0 min-h-96">
                {/* Time Column */}
                <div className="border-r border-gray-200">
                  {Array.from({ length: 11 }, (_, index) => {
                    const hour = index + 9; // Start from 9 AM (9, 10, 11, ..., 19)
                    return (
                      <div key={hour} className="h-16 border-b border-gray-100 p-2 text-xs text-gray-500">
                        {hour.toString().padStart(2, '0')}:00
                      </div>
                    );
                  })}
                </div>

                {/* Day Columns */}
                {weekDays.map((day, dayIndex) => {
                  const dayEvents = getEventsForDate(day);
                  
                  return (
                    <div key={dayIndex} className="border-r border-gray-200 last:border-r-0 relative">
                      {/* Hour grid */}
                      {Array.from({ length: 11 }, (_, index) => {
                        const hour = index + 9; // Start from 9 AM
                        return (
                          <div key={hour} className="h-16 border-b border-gray-100"></div>
                        );
                      })}
                      
                      {/* Events */}
                      <div className="absolute inset-0 p-1">
                        {dayEvents
                          .filter(event => {
                            const startHour = parseInt(event.startTime.split(':')[0]);
                            return startHour >= 9 && startHour <= 19; // Only show events between 9 AM and 7 PM
                          })
                          .sort((a, b) => a.startTime.localeCompare(b.startTime))
                          .reduce((acc: JSX.Element[], event, eventIndex) => {
                            const startHour = parseInt(event.startTime.split(':')[0]);
                            const startMinute = parseInt(event.startTime.split(':')[1]);
                            const endHour = parseInt(event.endTime.split(':')[0]);
                            const endMinute = parseInt(event.endTime.split(':')[1]);

                            // Adjust for 9 AM start time - subtract 9 hours from positioning
                            const adjustedStartHour = Math.max(0, startHour - 9);
                            const baseTop = (adjustedStartHour * 64) + (startMinute / 60 * 64);
                          const height = ((endHour - startHour) * 64) + ((endMinute - startMinute) / 60 * 64);
                          
                            // Find events with the same start time to stack them
                            const sameTimeEvents = dayEvents.filter(e =>
                              e.startTime === event.startTime
                            ).sort((a, b) => a.title.localeCompare(b.title));

                            const eventPosition = sameTimeEvents.findIndex(e => e.id === event.id);
                            const stackOffset = eventPosition * 38; // 38px offset between stacked events (height + spacing)
                            const top = baseTop + stackOffset;

                            acc.push(
                            <div
                              key={event.id}
                              className="absolute left-1 right-1 rounded-lg p-2 text-xs cursor-pointer hover:shadow-md transition-shadow overflow-hidden"
                              style={{
                                top: `${top}px`,
                                height: `${Math.max(height, 32)}px`,
                                backgroundColor: event.color + '20',
                                  borderLeft: `3px solid ${event.color}`,
                                  zIndex: 10 + eventIndex
                              }}
                              onClick={() => {
                                const issue = issues.find(i => i.id === event.id);
                                if (issue) onIssueClick(issue);
                              }}
                            >
                              <div className="font-medium text-gray-900 truncate">
                                {event.title}
                              </div>
                              <div className="text-gray-600 truncate">
                                {event.category}
                              </div>
                              <div className="flex items-center space-x-1 mt-1">
                                <Clock className="w-3 h-3 text-gray-400" />
                                <span className="text-gray-500">{event.duration}</span>
                              </div>
                            </div>
                          );

                            return acc;
                          }, [])}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Waiting List Sidebar */}
      {showWaitingList && (
        <div className="w-80 bg-white border-l border-gray-200 flex flex-col">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <h3 className="font-semibold text-gray-900">Waiting list</h3>
                <span className="bg-gray-100 text-gray-600 text-xs font-medium px-2 py-1 rounded-full">
                  {waitingListItems.length}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <button className="p-1 text-gray-400 hover:text-gray-600 rounded">
                  <Plus className="w-4 h-4" />
                </button>
                <button className="p-1 text-gray-400 hover:text-gray-600 rounded">
                  <Search className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setShowWaitingList(false)}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded"
                >
                  <MoreHorizontal className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {waitingListItems.map((item) => (
              <div
                key={item.id}
                className="bg-blue-50 border border-blue-200 rounded-lg p-3 hover:shadow-sm transition-shadow cursor-pointer"
              >
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-1">
                    {getStatusIcon(item.status)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-gray-900 text-sm leading-tight mb-1">
                      {item.title}
                    </h4>
                    <div className="text-xs text-blue-600 mb-2">
                      {item.category}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">{item.duration}</span>
                      <img
                        src={getAssigneeAvatar(item.assignee)}
                        alt={item.assignee}
                        className="w-5 h-5 rounded-full border border-gray-200"
                        title={item.assignee}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};